import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fileAPI } from '../lib/api';
import ConfirmDialog from './ConfirmDialog';
import FileEditModal from './FileEditModal';
import FileShareModal from './FileShareModal';

const FileList = ({ files, onFileDeleted, onFileDownload, onFilePreview, showActions = true, onDeleteConfirm, onFileUpdated }) => {
	const { user } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('ALL');
	const [editingFile, setEditingFile] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [sharingFile, setSharingFile] = useState(null);
	const [showShareModal, setShowShareModal] = useState(false);
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleString();
	};

	const getCategoryLabel = (category) => {
		if (!category) return 'General';
		// Convert category value to readable label
		const categoryMap = {
			'PITCH_DECK': 'Pitch Deck',
			'INVESTOR_PRESENTATION': 'Investor Presentation',
			'EXECUTIVE_SUMMARY': 'Executive Summary',
			'FINANCIAL': 'Financial Statements',
			'AUDITED_FINANCIALS': 'Audited Financials',
			'PROJECTED_FINANCIALS': 'Projected Financials',
			'BALANCE_SHEET': 'Balance Sheet',
			'P_L_STATEMENT': 'P&L Statement',
			'CASH_FLOW_STATEMENT': 'Cash Flow Statement',
			'VALUATION_CERTIFICATE': 'Valuation Certificate',
			'CAP_TABLE': 'Cap Table',
			'LEGAL': 'Legal Documents',
			'INCORPORATION': 'Incorporation Documents',
			'AGREEMENT': 'Agreements',
			'SHA': 'Shareholder Agreement',
			'TERM_SHEET': 'Term Sheet',
			'CCD': 'CCD',
			'SAFE_AGREEMENT': 'SAFE Agreement',
			'CONVERTIBLE_NOTE': 'Convertible Note',
			'EMPLOYMENT_AGREEMENT': 'Employment Agreement',
			'NDA': 'NDA',
			'IP_DOCUMENTS': 'IP Documents',
			'PATENTS': 'Patents',
			'TRADEMARKS': 'Trademarks',
			'BUSINESS_PLAN': 'Business Plan',
			'MARKET_RESEARCH': 'Market Research',
			'COMPETITIVE_ANALYSIS': 'Competitive Analysis',
			'BUSINESS_MODEL': 'Business Model',
			'GO_TO_MARKET': 'Go-to-Market',
			'PRODUCT_DOCUMENTATION': 'Product Docs',
			'TECHNICAL_SPECS': 'Technical Specs',
			'COMPLIANCE': 'Compliance',
			'LICENSES': 'Licenses',
			'REGULATORY_FILINGS': 'Regulatory Filings',
			'TAX_DOCUMENTS': 'Tax Documents',
			'OPERATIONAL': 'Operational',
			'HR_DOCUMENTS': 'HR Documents',
			'POLICIES': 'Policies',
			'PROCESSES': 'Processes',
			'MARKETING_MATERIALS': 'Marketing',
			'SALES_DECK': 'Sales Deck',
			'BRAND_GUIDELINES': 'Brand Guidelines',
			'GENERAL': 'General',
			'OTHER': 'Other'
		};
		return categoryMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
	};

	const getCategoryColor = (category) => {
		if (!category) return 'bg-gray-100 text-gray-800';
		// Color coding by category type
		if (category.includes('PITCH') || category.includes('PRESENTATION') || category.includes('DECK')) {
			return 'bg-purple-100 text-purple-800';
		} else if (category.includes('FINANCIAL') || category.includes('VALUATION') || category.includes('CAP_TABLE') || category.includes('BALANCE') || category.includes('CASH_FLOW') || category.includes('P_L')) {
			return 'bg-green-100 text-green-800';
		} else if (category.includes('LEGAL') || category.includes('AGREEMENT') || category.includes('SHA') || category.includes('TERM') || category.includes('NDA') || category.includes('PATENT') || category.includes('TRADEMARK') || category.includes('IP')) {
			return 'bg-blue-100 text-blue-800';
		} else if (category.includes('BUSINESS') || category.includes('MARKET') || category.includes('COMPETITIVE') || category.includes('PRODUCT') || category.includes('TECHNICAL')) {
			return 'bg-orange-100 text-orange-800';
		} else if (category.includes('COMPLIANCE') || category.includes('LICENSE') || category.includes('REGULATORY') || category.includes('TAX')) {
			return 'bg-yellow-100 text-yellow-800';
		} else if (category.includes('OPERATIONAL') || category.includes('HR') || category.includes('POLICY') || category.includes('PROCESS')) {
			return 'bg-indigo-100 text-indigo-800';
		} else if (category.includes('MARKETING') || category.includes('SALES') || category.includes('BRAND')) {
			return 'bg-pink-100 text-pink-800';
		} else {
			return 'bg-gray-100 text-gray-800';
		}
	};

	// Get all unique categories from files
	const getAllCategories = () => {
		const categories = new Set();
		files.forEach(file => {
			if (file.fileCategory) {
				categories.add(file.fileCategory);
			}
		});
		return Array.from(categories).sort();
	};

	// Filter files based on search term and category
	const filteredFiles = files.filter(file => {
		const matchesSearch = !searchTerm ||
			file.originalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			getCategoryLabel(file.fileCategory)?.toLowerCase().includes(searchTerm.toLowerCase());

		const matchesCategory = selectedCategory === 'ALL' || file.fileCategory === selectedCategory;

		return matchesSearch && matchesCategory;
	});

	const getFileIcon = (contentType) => {
		if (contentType?.startsWith('image/')) {
			return (
				<svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
				</svg>
			);
		} else if (contentType?.includes('pdf')) {
			return (
				<svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
				</svg>
			);
		} else if (contentType?.includes('word') || contentType?.includes('document')) {
			return (
				<svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
				</svg>
			);
		} else if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) {
			return (
				<svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M9 17v1a2 2 0 002 2h6a2 2 0 002-2v-1M9 7v1a2 2 0 002 2h6a2 2 0 002-2V7M9 7h10a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2zm0 0v6" />
				</svg>
			);
		} else if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) {
			return (
				<svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4" />
				</svg>
			);
		} else {
			return (
				<svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
				</svg>
			);
		}
	};

	const handleDownload = async (fileId, fileName) => {
		setLoading(true);
		setError(null);
		try {
			const response = await fileAPI.getFileDownload(fileId);
			// Create blob from response data
			const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
			// Create object URL
			const url = window.URL.createObjectURL(blob);
			// Create download link
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName || 'download';
			document.body.appendChild(link);
			link.click();
			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			onFileDownload && onFileDownload(fileId);
		} catch (err) {
			setError(err.message || 'Failed to download file');
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = async (fileId) => {
		setLoading(true);
		setError(null);
		try {
			const response = await fileAPI.getFilePreview(fileId);
			window.open(response.previewUrl, '_blank');
			onFilePreview && onFilePreview(fileId);
		} catch (err) {
			setError(err.message || 'Failed to preview file');
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (fileId, fileName) => {
		const deleteAction = async () => {
			setLoading(true);
			setError(null);
			try {
				await fileAPI.deleteFile(fileId);
				onFileDeleted && onFileDeleted(fileId);
			} catch (err) {
				setError(err.message || 'Failed to delete file');
			} finally {
				setLoading(false);
			}
		};

		if (onDeleteConfirm) {
			onDeleteConfirm(fileId, fileName, deleteAction);
		} else {
			setConfirm({
				isOpen: true,
				title: 'Delete File',
				message: `Are you sure you want to delete "${fileName}"?`,
				type: 'danger',
				onConfirm: deleteAction
			});
		}
	};

	// Search and filter controls
	const searchAndFilterControls = (
		<div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Search Input */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Search Files
					</label>
					<div className="relative">
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
						</div>
						<input
							type="text"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder="Search by name, description, or document type..."
							className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
						/>
					</div>
				</div>

				{/* Category Filter */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Filter by Document Type
					</label>
					<select
						value={selectedCategory}
						onChange={(e) => setSelectedCategory(e.target.value)}
						className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
					>
						<option value="ALL">All Document Types</option>
						{getAllCategories().map(category => (
							<option key={category} value={category}>
								{getCategoryLabel(category)}
							</option>
						))}
					</select>
				</div>
			</div>

			{/* Results count */}
			{(searchTerm || selectedCategory !== 'ALL') && (
				<div className="mt-3 text-sm text-gray-600">
					Showing {filteredFiles.length} of {files.length} file(s)
					{(searchTerm || selectedCategory !== 'ALL') && (
						<button
							onClick={() => {
								setSearchTerm('');
								setSelectedCategory('ALL');
							}}
							className="ml-2 text-blue-600 hover:text-blue-800 underline"
						>
							Clear filters
						</button>
					)}
				</div>
			)}
		</div>
	);

	if (files.length === 0) {
		return (
			<div className="text-center py-12">
				<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
						d="M9 13h6m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
				</svg>
				<h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
				<p className="mt-1 text-sm text-gray-500">No files have been uploaded yet.</p>
			</div>
		);
	}

	return (
		<div className="file-list">
			{/* Search and Filter Controls */}
			{searchAndFilterControls}

			{error && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
					<div className="flex">
						<div className="flex-shrink-0">
							<svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
							</svg>
						</div>
						<div className="ml-3">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					</div>
				</div>
			)}

			<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
				<table className="min-w-full divide-y divide-gray-300">
					<thead className="bg-gray-50">
						<tr>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								File
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Document Type
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Size
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Uploaded By
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Uploaded At
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Permissions
							</th>
							{showActions && (
								<th scope="col" className="relative px-6 py-3">
									<span className="sr-only">Actions</span>
								</th>
							)}
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{filteredFiles.length === 0 ? (
							<tr>
								<td colSpan={showActions ? 7 : 6} className="px-6 py-12 text-center">
									<svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
									<h3 className="mt-2 text-sm font-medium text-gray-900">No files found</h3>
									<p className="mt-1 text-sm text-gray-500">
										{searchTerm || selectedCategory !== 'ALL'
											? 'Try adjusting your search or filter criteria.'
											: 'No files match your criteria.'}
									</p>
								</td>
							</tr>
						) : (
							filteredFiles.map((file) => (
								<tr key={file.id} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="flex items-center">
											<div className="flex-shrink-0">
												{getFileIcon(file.contentType)}
											</div>
											<div className="ml-4">
												<div className="text-sm font-medium text-gray-900">
													{file.originalName}
												</div>
												{file.description && (
													<div className="text-xs text-gray-500 mt-1">
														{file.description}
													</div>
												)}
											</div>
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap">
										<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(file.fileCategory)}`}>
											{getCategoryLabel(file.fileCategory)}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatFileSize(file.fileSize)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										<div className="flex items-center">
											<div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
												<span className="text-blue-600 text-xs font-medium">
													{(file.uploadedByName || file.uploadedBy || 'Unknown')?.charAt(0)?.toUpperCase()}
												</span>
											</div>
											<div>
												<div className="text-sm font-medium text-gray-900">
													{file.uploadedByName || file.uploadedBy || 'Unknown'}
												</div>
												{file.uploadedByEmail && (
													<div className="text-xs text-gray-500">
														{file.uploadedByEmail}
													</div>
												)}
											</div>
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatDate(file.uploadedAt)}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
										{file.permissions && file.permissions.length > 0 ? (
											<div className="flex items-center space-x-1">
												<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
													{file.permissionCount || file.permissions.length} {file.permissionCount === 1 ? 'permission' : 'permissions'}
												</span>
												<button
													className="text-blue-600 hover:text-blue-800"
													title={`View permissions: ${file.permissions.map(p => p.userName || p.role || 'Network').join(', ')}`}
												>
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
													</svg>
												</button>
											</div>
										) : (
											<span className="text-xs text-gray-400">No permissions</span>
										)}
									</td>
									{showActions && (
										<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
											<div className="flex justify-end space-x-2">
												<button
													onClick={() => handlePreview(file.id)}
													disabled={loading}
													className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
													title="Preview"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
															d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
															d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
													</svg>
												</button>
												<button
													onClick={() => {
														setEditingFile(file);
														setShowEditModal(true);
													}}
													disabled={loading}
													className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
													title="Edit"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
															d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
													</svg>
												</button>
												<button
													onClick={() => handleDownload(file.id, file.originalName)}
													disabled={loading}
													className="text-green-600 hover:text-green-900 disabled:opacity-50"
													title="Download"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
															d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
													</svg>
												</button>
												<button
													onClick={() => {
														setSharingFile(file);
														setShowShareModal(true);
													}}
													disabled={loading}
													className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
													title="Share"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
															d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
													</svg>
												</button>
												{(user?.id === file.uploadedById ||
													user?.role === 'ADMIN' ||
													user?.role === 'STARTUP' ||
													(file.permissions && file.permissions.some(p => p.userId === user?.id && p.permissionType === 'DELETE'))) && (
														<button
															onClick={() => handleDelete(file.id, file.originalName)}
															disabled={loading}
															className="text-red-600 hover:text-red-900 disabled:opacity-50"
															title="Delete only available for uploader"
														>
															<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
																	d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
															</svg>
														</button>
													)}
											</div>
										</td>
									)}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Edit Modal */}
			<FileEditModal
				file={editingFile}
				isOpen={showEditModal}
				onClose={() => {
					setShowEditModal(false);
					setEditingFile(null);
				}}
				onSuccess={() => {
					setShowEditModal(false);
					setEditingFile(null);
					onFileUpdated && onFileUpdated();
				}}
			/>

			{/* Share Modal */}
			<FileShareModal
				file={sharingFile}
				isOpen={showShareModal}
				onClose={() => {
					setShowShareModal(false);
					setSharingFile(null);
				}}
				onSuccess={() => {
					setShowShareModal(false);
					setSharingFile(null);
					onFileUpdated && onFileUpdated();
				}}
			/>

			{/* Confirm Dialog */}
			<ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
			/>
		</div >
	);
};

export default FileList;